import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Landing from "./pages/Landing";
import AdminGalicia from "./pages/AdminGalicia";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/* Navbar                                                                     */
/* -------------------------------------------------------------------------- */
const Nav = () => (
  <header className="nav" data-testid="navbar">
    <div className="row">
      <Link to="/" className="brand" data-testid="brand-home">
        <div className="logo">C</div>
        <div>
          Concello360
          <small>Formación municipal</small>
        </div>
      </Link>
      <nav className="nav-links">
        <a className="nav-pill" href="/#bloques" data-testid="link-bloques">Bloques</a>
        <a className="nav-pill" href="/#metodologia" data-testid="link-metodologia">Metodología</a>
        <a className="nav-pill" href="/#scorm" data-testid="link-scorm">SCORM &amp; Moodle</a>
        <a className="nav-pill nav-pill-cta" href="/#solicitar" data-testid="link-solicitar">Solicitar formación</a>
      </nav>
    </div>
  </header>
);

/* -------------------------------------------------------------------------- */
/* Home                                                                       */
/* -------------------------------------------------------------------------- */
const Home = () => {
  const [catalog, setCatalog] = useState(null);
  const [courseStatus, setCourseStatus] = useState({});
  const [leadOpen, setLeadOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/catalog`).then(r => {
      setCatalog(r.data);
      // Cargamos status del curso piloto y de cualquier curso conocido
      const promises = r.data.blocks.flatMap(b =>
        b.courses.map(c =>
          axios.get(`${API}/courses/${c.id}/status`).then(s => [c.id, s.data]).catch(() => [c.id, {status: "not_generated"}])
        )
      );
      Promise.all(promises).then(arr => {
        const obj = {};
        arr.forEach(([id, st]) => { obj[id] = st; });
        setCourseStatus(obj);
      });
    });
  }, []);

  if (!catalog) {
    return (
      <div className="hero" data-testid="loading-home">
        <div><span className="spinner" /> Cargando catálogo…</div>
      </div>
    );
  }

  return (
    <>
      <Nav />
      <section className="hero" data-testid="hero-section">
        <div>
          <div className="kicker">Plataforma de formación 100% SCORM · Moodle ready</div>
          <h1>
            Formación práctica para <em>ayuntamientos</em> que quieren hacer las cosas bien.
          </h1>
          <p className="lead">
            33 cursos divididos en 6 bloques estratégicos. Cada curso son 21 horas
            de contenido extenso, con prosa real, ejemplos de ayuntamientos
            gallegos y españoles, casos prácticos, retroalimentación pedagógica
            y exámenes en formato GIFT. Empaquetado en SCORM 1.2 listo para
            subir a Moodle.
          </p>
          <div className="ctas">
            <a className="btn" href="#bloques" data-testid="cta-explore">
              Explorar los 6 bloques
            </a>
            <button className="btn accent" onClick={() => setLeadOpen(true)} data-testid="cta-request-home">
              Solicitar formación
            </button>
            <Link to="/cursos/expediente-electronico" className="btn ghost" data-testid="cta-pilot">
              Ver curso piloto
            </Link>
          </div>
        </div>
        <aside className="hero-stats">
          <h3>El proyecto en cifras</h3>
          <div className="grid">
            <div className="stat"><span className="n">{catalog.total_blocks}</span><span className="l">Bloques temáticos</span></div>
            <div className="stat"><span className="n">{catalog.total_courses}</span><span className="l">Cursos diseñados</span></div>
            <div className="stat"><span className="n">{catalog.total_hours}</span><span className="l">Horas formativas</span></div>
            <div className="stat"><span className="n">{catalog.total_courses * 4}</span><span className="l">SCORMs por entregar</span></div>
          </div>
        </aside>
      </section>

      <section className="blocks" id="bloques" data-testid="blocks-section">
        <div className="section-head">
          <div>
            <h2>Bloques y cursos</h2>
            <p className="desc">Pulsa un curso para ver detalle, descargas SCORM y examen GIFT. El curso piloto <strong>Expediente Electrónico</strong> está totalmente desarrollado.</p>
          </div>
        </div>

        <div className="block-list">
          {catalog.blocks.map((b, idx) => (
            <article key={b.id} className="block" data-testid={`block-${b.id}`}>
              <header className="block-head" style={{ background: `linear-gradient(135deg, ${b.color} 0%, ${b.color}dd 100%)` }}>
                <div className="num">{b.code}</div>
                <div>
                  <h3>{b.title}</h3>
                  <p>{b.subtitle}</p>
                </div>
                <div className="count">{b.courses.length} cursos · {b.courses.length * 21} h</div>
              </header>
              <div className="block-courses">
                {b.courses.map(c => {
                  const st = courseStatus[c.id] || { status: "loading" };
                  const cls = st.status === "ready" ? "ready" : (st.status === "partial" ? "partial" : "");
                  const label = st.status === "ready" ? "Listo" : (st.status === "partial" ? `${st.cached_sections}/48 secciones` : "Pendiente");
                  return (
                    <button
                      key={c.id}
                      className={`course-cell ${cls}`}
                      data-testid={`course-${c.id}`}
                      onClick={() => navigate(`/cursos/${c.id}`)}
                    >
                      <div className="title">{c.title}</div>
                      <div className="info">
                        <span className="dot"></span>
                        <span>{c.hours} h · 4 SCORMs · GIFT</span>
                        <span className="status-badge">{label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="blocks" id="metodologia" data-testid="methodology-section">
        <div className="section-head">
          <div>
            <h2>Metodología</h2>
            <p className="desc">Cada curso son 21 h reales de aprendizaje, agrupadas en 4 temas. Cada tema es un SCORM 1.2 independiente con navegación lateral, seguimiento de visualización y autoevaluación.</p>
          </div>
        </div>

        <div className="block-courses" style={{ background: 'var(--c-line)', borderRadius: '16px', overflow: 'hidden' }}>
          {[
            {n: '01', t: 'Investigación normativa', d: 'Cada apartado cita Ley 39/2015, Ley 40/2015, ENI, ENS y normativa europea cuando procede. Nada inventado.'},
            {n: '02', t: 'Prosa real, no esquemas', d: 'Párrafos largos, ejemplos concretos de ayuntamientos y experiencias de campo, no listas vacías ni viñetas sueltas.'},
            {n: '03', t: 'Visual de verdad', d: 'Acordeones desplegables, tarjetas hover, callouts laterales, línea de tiempo, tooltips, glosario animado.'},
            {n: '04', t: 'Seguimiento Moodle', d: 'Cada SCORM 1.2 envía cmi.core.lesson_status y progreso al LMS. Compatible con Moodle 3.9 a 4.x.'},
            {n: '05', t: 'Autoevaluación inmediata', d: 'Cada tema cierra con 8 preguntas con retroalimentación específica de acierto y de error.'},
            {n: '06', t: 'Examen final GIFT', d: '30 preguntas por curso en formato GIFT con feedback. Importas el archivo en Moodle y queda integrado en minutos.'},
          ].map(c => (
            <div key={c.n} className="course-cell" style={{ cursor: 'default' }}>
              <div style={{ fontFamily: 'var(--font-display)', color: 'var(--c-accent)', fontSize: '1.6rem', fontWeight: 500, lineHeight: 1 }}>{c.n}</div>
              <div className="title">{c.t}</div>
              <div className="info"><span>{c.d}</span></div>
            </div>
          ))}
        </div>
      </section>

      <section className="blocks" id="scorm" data-testid="scorm-section">
        <div className="exam-card" style={{ marginTop: 0 }}>
          <div>
            <h3>SCORM 1.2 · Moodle Ready</h3>
            <h2>Sube y entrega, sin fricción técnica.</h2>
            <p>Cada paquete incluye imsmanifest.xml, todos los assets necesarios y un wrapper SCORM 1.2 que registra <code style={{color: '#fbecc8', background: 'rgba(0,0,0,.2)', padding: '1px 6px', borderRadius: 4}}>cmi.core.lesson_status</code>, el progreso por apartado y el estado de completado en tu Moodle.</p>
          </div>
          <div className="actions">
            <Link to="/cursos/expediente-electronico" className="btn accent" data-testid="cta-pilot-2">Ver curso piloto →</Link>
          </div>
        </div>
      </section>

      <section className="blocks" id="solicitar" data-testid="request-section">
        <div className="request-card">
          <div className="request-copy">
            <span className="kicker-accent">Reservar formación · presupuesto en 48 h</span>
            <h2>¿Lo quieres para tu ayuntamiento?</h2>
            <p>
              Te enviamos los SCORMs listos para tu Moodle, los exámenes GIFT importables y el plan de despliegue para tu plantilla. Sin permanencia, sin licencias eternas, sin alquileres opacos: el contenido es tuyo en cuanto entregamos.
            </p>
            <ul className="request-bullets">
              <li>Hasta <strong>{catalog.total_courses} cursos</strong> con {catalog.total_hours} horas de formación oficial</li>
              <li>Compatibilidad <strong>Moodle 3.9 a 4.x</strong> y plataformas LMS estándar</li>
              <li>Opción de <strong>versión galega</strong> y adaptaciones a normativa autonómica</li>
              <li>Asesoría incluida para encajar la formación en FORMAN, FEDAP o fondos NextGen</li>
            </ul>
            <div className="ctas">
              <button className="btn accent" onClick={() => setLeadOpen(true)} data-testid="cta-request-bottom">
                Pedir presupuesto
              </button>
              <a className="btn ghost" href="mailto:hola@concello360.es" data-testid="cta-email">
                hola@concello360.es
              </a>
            </div>
          </div>
          <aside className="request-side">
            <div className="request-stat">
              <span className="n">48 h</span>
              <span className="l">Tiempo de respuesta con propuesta cerrada</span>
            </div>
            <div className="request-stat">
              <span className="n">100 %</span>
              <span className="l">Contenido propio, sin terceros, sin trazas externas</span>
            </div>
            <div className="request-stat">
              <span className="n">21 h</span>
              <span className="l">Por curso, equivalente a una semana laboral formativa</span>
            </div>
          </aside>
        </div>
      </section>

      {leadOpen && <LeadModal context={{ source: "home" }} onClose={() => setLeadOpen(false)} />}

      <footer className="foot">
        Concello360 · Diseñado para ayuntamientos · {new Date().getFullYear()}
      </footer>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/* Course Detail                                                              */
/* -------------------------------------------------------------------------- */
const CourseDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [openTheme, setOpenTheme] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [leadOpen, setLeadOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/courses/${id}`).then(r => setData(r.data));
  }, [id]);

  if (!data) return (
    <>
      <Nav />
      <div className="detail" data-testid="loading-course"><span className="spinner" /> Cargando curso…</div>
    </>
  );

  const { block, course_ref, outline, status } = data;
  const isReady = status.status === "ready";
  const themeStatusMap = {};
  (status.themes || []).forEach(t => { themeStatusMap[t.id] = t; });

  // Fallback si el curso aún no tiene outline (sólo el piloto la tiene)
  const themes = outline?.themes || [];
  const summary = outline?.objective || `${course_ref?.title}: curso de ${course_ref?.hours} horas dividido en 4 temas SCORM con examen final en GIFT.`;

  return (
    <>
      <Nav />
      <main className="detail" data-testid="course-detail">
        <div className="breadcrumbs">
          <Link to="/">Concello360</Link> · {block?.title}
        </div>
        <h1>{course_ref?.title || id}</h1>
        <p className="summary">{summary}</p>

        <div className="detail-cta">
          <button className="btn accent" onClick={() => setLeadOpen(true)} data-testid="btn-request-course">
            Solicitar esta formación para mi ayuntamiento
          </button>
          <span className="detail-cta-note">Te enviamos propuesta cerrada en menos de 48 h laborables.</span>
        </div>

        <div className="detail-meta">
          <div><strong>{course_ref?.hours} h</strong> Duración total</div>
          <div><strong>4 SCORMs</strong> Uno por tema</div>
          <div><strong>30 preguntas</strong> Examen final GIFT</div>
          <div><strong>SCORM 1.2</strong> Moodle 3.9 a 4.x</div>
          <div><strong>{block?.title}</strong> Bloque {block?.code}</div>
        </div>

        {!isReady && (
          <div className="empty" data-testid="not-generated">
            {status.status === "partial"
              ? <>Este curso tiene contenido en proceso de generación (<strong>{status.cached_sections}</strong> apartados en caché). Vuelve cuando termine.</>
              : <>Este curso está en hoja de ruta. El piloto desarrollado es <Link to="/cursos/expediente-electronico">Expediente Electrónico</Link>.</>}
          </div>
        )}

        {themes.length > 0 && (
          <div className="themes">
            {themes.map(t => {
              const ts = themeStatusMap[t.id];
              const ready = !!ts && !!ts.zip;
              return (
                <article key={t.id} className={`theme ${openTheme === t.id ? "open" : ""}`} data-testid={`theme-${t.id}`}>
                  <div className="theme-row">
                    <div className="num">{String(t.number).padStart(2, "0")}</div>
                    <div>
                      <span className={`theme-status ${ready ? "ready" : "partial"}`}>
                        {ready ? "SCORM disponible" : "En preparación"}
                      </span>
                      <h3>{t.title}</h3>
                      <p>{t.summary}</p>
                      <p style={{ marginTop: 6, fontSize: '.85rem', color: 'var(--c-muted)' }}>
                        {t.duration_hours} h · {t.sections.length} apartados
                        {ts?.html_lines ? ` · ${ts.html_lines} líneas HTML` : ""}
                      </p>
                    </div>
                    <div className="actions">
                      <button
                        className="btn ghost"
                        data-testid={`btn-toggle-${t.id}`}
                        onClick={() => setOpenTheme(openTheme === t.id ? null : t.id)}
                      >
                        {openTheme === t.id ? "Ocultar índice" : "Ver índice"}
                      </button>
                      {ready && (
                        <button
                          className="btn ghost"
                          data-testid={`btn-preview-${t.id}`}
                          onClick={() => setPreviewModal({course: id, theme: t.id, title: t.title})}
                        >
                          Previsualizar
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="theme-detail">
                    <ol>
                      {t.sections.map(s => <li key={s.id}>{s.title}</li>)}
                    </ol>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <article className="exam-card" data-testid="exam-card">
          <div>
            <h3>Examen final</h3>
            <h2>30 preguntas en formato GIFT con retroalimentación</h2>
            <p>Importa el archivo .gift directamente en el banco de preguntas de Moodle. Cada respuesta —correcta o incorrecta— incluye una explicación pedagógica que ayuda al alumno a entender el porqué.</p>
          </div>
          <div className="actions">
            {status.exam_gift ? (
              <>
                <a className="btn accent" href={`${API}/courses/${id}/exam.gift`} data-testid="btn-download-gift">
                  Descargar examen.gift
                </a>
                <button className="btn ghost" onClick={() => setPreviewModal({course: id, theme: '__gift__', title: 'Examen GIFT'})} data-testid="btn-preview-gift">
                  Previsualizar
                </button>
              </>
            ) : (
              <span style={{ color: '#fbecc8', opacity: .85 }}>Pendiente de generación</span>
            )}
          </div>
        </article>
      </main>

      {previewModal && <PreviewModal info={previewModal} onClose={() => setPreviewModal(null)} />}
      {leadOpen && <LeadModal context={{ course_id: id, course_title: course_ref?.title, source: "course-detail" }} onClose={() => setLeadOpen(false)} />}

      <footer className="foot">
        Concello360 · Curso {course_ref?.title} · {new Date().getFullYear()}
      </footer>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/* PreviewModal                                                               */
/* -------------------------------------------------------------------------- */
const PreviewModal = ({ info, onClose }) => {
  const [giftText, setGiftText] = useState(null);

  useEffect(() => {
    if (info.theme === '__gift__') {
      axios.get(`${API}/courses/${info.course}/exam.preview`).then(r => setGiftText(r.data));
    }
  }, [info]);

  return (
    <div className="modal-back" onClick={onClose} data-testid="preview-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header>
          <h3>{info.title}</h3>
          <button onClick={onClose} data-testid="close-preview">Cerrar</button>
        </header>
        <div className="body">
          {info.theme === '__gift__' ? (
            <pre>{giftText || 'Cargando examen…'}</pre>
          ) : (
            <iframe
              className="preview-frame"
              title={info.title}
              src={`${API}/courses/${info.course}/preview/${info.theme}/`}
              data-testid="preview-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Lead Form Modal                                                            */
/* -------------------------------------------------------------------------- */
const LeadModal = ({ context, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    phone: "",
    employees: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.email.trim() || !form.organization.trim()) {
      setError("Nombre, email y ayuntamiento/entidad son obligatorios.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      setError("El email no parece válido.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        course_id: context?.course_id || null,
        course_title: context?.course_title || null,
        source: context?.source || "portal",
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No hemos podido enviar tu solicitud. Inténtalo de nuevo en unos minutos.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-back" onClick={onClose} data-testid="lead-modal">
      <div className="modal lead" onClick={(e) => e.stopPropagation()}>
        <header>
          <h3>
            {context?.course_title
              ? <>Solicitar formación · <em style={{ color: 'var(--c-accent)', fontStyle: 'normal' }}>{context.course_title}</em></>
              : <>Solicitar formación para tu ayuntamiento</>}
          </h3>
          <button onClick={onClose} data-testid="close-lead">Cerrar</button>
        </header>
        <div className="body lead-body">
          {done ? (
            <div className="lead-thanks" data-testid="lead-thanks">
              <div className="lead-check">✓</div>
              <h4>Gracias, {form.name.split(" ")[0]}.</h4>
              <p>Hemos recibido tu solicitud y nos pondremos en contacto contigo en menos de <strong>48 horas laborables</strong> con una propuesta a medida para <strong>{form.organization}</strong>.</p>
              <button className="btn" onClick={onClose} data-testid="lead-close-success">Volver al catálogo</button>
            </div>
          ) : (
            <form onSubmit={submit} className="lead-form" data-testid="lead-form">
              <p className="lead-intro">
                Cuéntanos un poco sobre tu ayuntamiento y te enviamos una propuesta cerrada: matrícula en Moodle, soporte pedagógico y certificación. Sin compromiso.
              </p>
              <div className="lead-grid">
                <label>
                  <span>Nombre y apellidos *</span>
                  <input type="text" value={form.name} onChange={set("name")} required data-testid="lead-name" maxLength={120} />
                </label>
                <label>
                  <span>Email corporativo *</span>
                  <input type="email" value={form.email} onChange={set("email")} required data-testid="lead-email" maxLength={200} />
                </label>
                <label>
                  <span>Ayuntamiento / Entidad *</span>
                  <input type="text" value={form.organization} onChange={set("organization")} required data-testid="lead-organization" maxLength={160} placeholder="p. ej. Concello de Lugo" />
                </label>
                <label>
                  <span>Cargo o puesto</span>
                  <input type="text" value={form.role} onChange={set("role")} data-testid="lead-role" maxLength={120} placeholder="p. ej. Secretaría, RR.HH., concejalía…" />
                </label>
                <label>
                  <span>Teléfono</span>
                  <input type="tel" value={form.phone} onChange={set("phone")} data-testid="lead-phone" maxLength={40} placeholder="opcional, para contacto directo" />
                </label>
                <label>
                  <span>Personas a formar</span>
                  <select value={form.employees} onChange={set("employees")} data-testid="lead-employees">
                    <option value="">Selecciona…</option>
                    <option value="1-10">1 a 10</option>
                    <option value="11-25">11 a 25</option>
                    <option value="26-50">26 a 50</option>
                    <option value="51-100">51 a 100</option>
                    <option value="100+">Más de 100</option>
                  </select>
                </label>
              </div>
              <label className="lead-msg">
                <span>¿Qué necesitas? (opcional)</span>
                <textarea value={form.message} onChange={set("message")} data-testid="lead-message" maxLength={2000} rows={4} placeholder="Cuéntanos el contexto: número de cursos, fechas tentativas, integración con vuestro Moodle, financiación FORMAN/FEDAP…" />
              </label>
              {context?.course_title && (
                <div className="lead-context" data-testid="lead-context">
                  Estás solicitando información sobre <strong>{context.course_title}</strong>.
                </div>
              )}
              {error && <div className="lead-error" data-testid="lead-error">{error}</div>}
              <div className="lead-actions">
                <button type="button" className="btn ghost" onClick={onClose} data-testid="lead-cancel">Cancelar</button>
                <button type="submit" className="btn accent" disabled={submitting} data-testid="lead-submit">
                  {submitting ? <><span className="spinner" /> Enviando…</> : "Enviar solicitud"}
                </button>
              </div>
              <p className="lead-privacy">
                Trataremos tus datos sólo para responder a tu petición, conforme al RGPD. Nada de spam.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Admin: lista de leads                                                      */
/* -------------------------------------------------------------------------- */
const STATUS_LABEL = {
  new: "Nuevo",
  contacted: "Contactado",
  won: "Ganado",
  lost: "Perdido",
};

const AdminLeads = () => {
  const stored = typeof window !== "undefined" ? window.localStorage.getItem("c360_admin_token") : null;
  const [token, setToken] = useState(stored || "");
  const [tokenInput, setTokenInput] = useState("");
  const [authError, setAuthError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);

  const headers = { headers: { "X-Admin-Token": token } };

  const fetchAll = async (currentToken = token) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (query.trim()) params.set("q", query.trim());
      params.set("limit", "500");
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/leads?${params.toString()}`, { headers: { "X-Admin-Token": currentToken } }),
        axios.get(`${API}/admin/leads/stats`, { headers: { "X-Admin-Token": currentToken } }),
      ]);
      setLeads(l.data);
      setStats(s.data);
      setAuthError(null);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthError("Token inválido. Vuelve a iniciar sesión.");
        window.localStorage.removeItem("c360_admin_token");
        setToken("");
      } else {
        setAuthError("Error al cargar leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError(null);
    try {
      await axios.post(`${API}/admin/login`, { token: tokenInput.trim() });
      window.localStorage.setItem("c360_admin_token", tokenInput.trim());
      setToken(tokenInput.trim());
      fetchAll(tokenInput.trim());
    } catch (err) {
      setAuthError(err?.response?.status === 401 ? "Token incorrecto." : "No se ha podido validar.");
    }
  };

  const updateStatus = async (id, newStatus) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status: newStatus }, headers);
      setLeads(leads.map(l => l.id === id ? { ...l, status: newStatus } : l));
      // refresh stats
      const s = await axios.get(`${API}/admin/leads/stats`, headers);
      setStats(s.data);
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = () => {
    const url = `${API}/admin/leads/export.csv`;
    fetch(url, headers).then(r => r.blob()).then(blob => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `concello360-leads-${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
    });
  };

  const logout = () => {
    window.localStorage.removeItem("c360_admin_token");
    setToken("");
    setLeads([]);
    setStats(null);
  };

  if (!token) {
    return (
      <div className="admin-wrap" data-testid="admin-login-wrap">
        <div className="admin-login">
          <Link to="/" className="brand brand-light" data-testid="admin-brand-home">
            <div className="logo">C</div>
            <div>Concello360<small>Panel administración</small></div>
          </Link>
          <form onSubmit={handleLogin} data-testid="admin-login-form">
            <h2>Acceso administración</h2>
            <p>Introduce el token administrador para ver las solicitudes recibidas.</p>
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="Token administrador"
              required
              data-testid="admin-token-input"
            />
            {authError && <div className="lead-error" data-testid="admin-login-error">{authError}</div>}
            <button type="submit" className="btn accent" data-testid="admin-login-submit">Entrar</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-wrap" data-testid="admin-leads">
      <header className="admin-bar">
        <Link to="/" className="brand brand-light" data-testid="admin-back-home">
          <div className="logo">C</div>
          <div>Concello360<small>Panel administración</small></div>
        </Link>
        <div className="admin-bar-actions">
          <Link to="/admin/galicia" className="btn ghost" data-testid="admin-link-galicia">🗺️ Concellos Galicia</Link>
          <button className="btn ghost" onClick={() => fetchAll()} data-testid="admin-refresh">↻ Actualizar</button>
          <button className="btn" onClick={exportCsv} data-testid="admin-export-csv">Exportar CSV</button>
          <button className="btn ghost" onClick={logout} data-testid="admin-logout">Salir</button>
        </div>
      </header>

      {stats && (
        <section className="admin-stats" data-testid="admin-stats">
          <div className="stat-card">
            <span className="n">{stats.total}</span>
            <span className="l">Total recibidos</span>
          </div>
          {["new", "contacted", "won", "lost"].map(s => (
            <div key={s} className={`stat-card status-${s}`} onClick={() => setFilter(filter === s ? "" : s)} data-testid={`stat-${s}`}>
              <span className="n">{stats.by_status[s] || 0}</span>
              <span className="l">{STATUS_LABEL[s]}{filter === s ? " · activo" : ""}</span>
            </div>
          ))}
        </section>
      )}

      <section className="admin-filters">
        <input
          type="search"
          placeholder="Buscar por nombre, email, ayuntamiento, curso o mensaje…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") fetchAll(); }}
          data-testid="admin-search-input"
        />
        <button className="btn" onClick={() => fetchAll()} data-testid="admin-search-btn">Buscar</button>
        {(filter || query) && (
          <button className="btn ghost" onClick={() => { setFilter(""); setQuery(""); setTimeout(() => fetchAll(), 0); }} data-testid="admin-clear-filters">Limpiar filtros</button>
        )}
      </section>

      {loading ? (
        <div className="admin-empty" data-testid="admin-loading">Cargando…</div>
      ) : leads.length === 0 ? (
        <div className="admin-empty" data-testid="admin-empty">Aún no hay solicitudes que coincidan.</div>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Quién</th>
                <th>Ayuntamiento</th>
                <th>Curso</th>
                <th>Personas</th>
                <th>Mensaje</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                  <td className="nowrap">{new Date(l.created_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                  <td>
                    <strong>{l.name}</strong>
                    <br />
                    <a href={`mailto:${l.email}`} className="muted-link">{l.email}</a>
                    {l.phone && <><br /><span className="muted">{l.phone}</span></>}
                    {l.role && <><br /><span className="muted">{l.role}</span></>}
                  </td>
                  <td><strong>{l.organization}</strong><br /><span className="muted">vía {l.source}</span></td>
                  <td>{l.course_title || <span className="muted">—</span>}</td>
                  <td className="nowrap">{l.employees || <span className="muted">—</span>}</td>
                  <td className="lead-msg-cell">{l.message || <span className="muted">—</span>}</td>
                  <td>
                    <select
                      value={l.status || "new"}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      disabled={busyId === l.id}
                      className={`status-pill status-${l.status || "new"}`}
                      data-testid={`status-${l.id}`}
                    >
                      <option value="new">Nuevo</option>
                      <option value="contacted">Contactado</option>
                      <option value="won">Ganado</option>
                      <option value="lost">Perdido</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/catalogo" element={<Home />} />
          <Route path="/cursos/:id" element={<CourseDetail />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin/galicia" element={<AdminGalicia />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
